/**
 * apiClient.js — Centralized Axios HTTP client with auth token management.
 *
 * AUTH FLOW OVERVIEW:
 *   1. INITIALIZATION: On module load, initAuth() fires immediately. In dev mode
 *      it auto-fetches a dev token from /auth/token/dev. In Cognito mode it no-ops
 *      (auth is handled by AuthContext + Amplify).
 *   2. REQUEST INTERCEPTOR: Every outgoing request waits for initAuth() to resolve,
 *      then calls getFreshToken() to inject a Bearer token into the Authorization header.
 *   3. 401 RESPONSE INTERCEPTOR: On a 401 response, the interceptor attempts token refresh:
 *      - Cognito mode: calls cognitoGetIdToken() (Amplify handles refresh internally).
 *      - JWT mode: posts the refresh_token to /auth/refresh.
 *      - Dev fallback: re-obtains a dev token from /auth/token/dev.
 *   4. QUEUE: While a refresh is in-flight, subsequent 401s are queued (failedQueue)
 *      and replayed once the new token is available.
 *
 * RACE CONDITION WARNING:
 *   Both the request interceptor (getFreshToken) and the 401 interceptor can call
 *   cognitoGetIdToken() concurrently. Amplify's fetchAuthSession() is internally
 *   deduped, so this is safe in practice. However, if you replace cognitoGetIdToken
 *   with a non-idempotent refresh call, you risk issuing two refresh requests
 *   simultaneously — one from the interceptor and one from AuthContext.refreshUser().
 *
 * SIDE-EFFECT WARNINGS:
 *   - Changing TOKEN_KEY or REFRESH_TOKEN_KEY will invalidate all existing sessions
 *     and break AuthContext (which imports getToken/setToken/clearToken).
 *   - Changing the timeout (120000ms) affects ALL api.js calls. Some endpoints
 *     override this per-request (e.g. document generation uses 600000ms).
 *   - The dev auto-login credentials ('dev-user', 'dev@localhost', admin, eng-dept)
 *     are duplicated in AuthContext.devLogin(). Changing them here without updating
 *     AuthContext will cause mismatched sessions.
 *
 * CONSUMERS:
 *   - api.js (all endpoints go through this client)
 *   - AuthContext.js (imports getToken, setToken, clearToken, setRefreshToken)
 *   - ChatContainer.js (imports client + getFreshToken for streaming)
 *   - OrchestratorView.js (imports client + getFreshToken for streaming)
 *   - CADChatPanel.js, DrawingList.js, APSViewer.js, CADViewerView.js,
 *     JobStatusIndicator.js (import client + waitForAuth for CAD operations)
 *   - SettingsPage.js, SearchBox.js, ManageDrawingsDialog.js, DrawingAnalysisPanel.js,
 *     CADViewerArea.js, CADFileList.js (import client directly)
 */
import axios from 'axios';
import { isCognitoConfigured, cognitoGetIdToken } from './cognitoAuth';

// Base URL for all API requests. Every call in api.js is relative to this.
// Side-effect: changing this requires the backend to be accessible at the new URL.
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const client = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    // Default timeout — individual api.js calls can override (e.g. file uploads).
    timeout: 60000, // 60s default
});

// ─── Token Storage ──────────────────────────────────────────────────────────
// localStorage keys. AuthContext also reads/writes these via the exported helpers.
// Side-effect: renaming these keys logs out every user with an existing session.
const TOKEN_KEY = 'eis_access_token';
const REFRESH_TOKEN_KEY = 'eis_refresh_token';

// Get token from storage
export const getToken = () => localStorage.getItem(TOKEN_KEY);

// Get refresh token from storage
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

// Set token in storage. Passing null/undefined removes the key.
// Called by: AuthContext (on login, refresh), getFreshToken (Cognito sync),
//            initAuth (dev auto-login), 401 interceptor (on successful refresh).
export const setToken = (token) => {
    if (token) {
        localStorage.setItem(TOKEN_KEY, token);
    } else {
        localStorage.removeItem(TOKEN_KEY);
    }
};

// Set refresh token in storage. Only relevant in JWT mode (Cognito manages its own refresh tokens).
export const setRefreshToken = (token) => {
    if (token) {
        localStorage.setItem(REFRESH_TOKEN_KEY, token);
    } else {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
};

// Clear all tokens. Called by AuthContext.logout() and during dev-mode re-login on 401.
// Side-effect: immediately invalidates the session; the next API call will trigger a 401.
export const clearToken = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// Check if we have a valid token
export const hasToken = () => !!getToken();

/**
 * Get a fresh token suitable for API calls.
 * - Cognito mode: delegates to cognitoGetIdToken() which calls Amplify's
 *   fetchAuthSession(). Amplify transparently refreshes expired tokens.
 *   The returned token is also written to localStorage to keep it in sync
 *   for components that read getToken() directly.
 * - JWT/dev mode: simply returns whatever is in localStorage.
 *
 * WARNING: This is called on EVERY request via the request interceptor.
 * In Cognito mode, fetchAuthSession() is an async network call (cached by Amplify).
 * Do not add heavy logic here.
 */
export const getFreshToken = async () => {
    if (isCognitoConfigured()) {
        const token = await cognitoGetIdToken();
        if (token) {
            setToken(token); // Keep localStorage in sync
        }
        return token;
    }
    return getToken();
};

// ─── Auth Initialization ────────────────────────────────────────────────────
// These module-level variables coordinate the one-time auth bootstrap.
// authInitPromise is awaited by the request interceptor to block API calls
// until initialization completes.
let authInitPromise = null;
let authInitialized = false;

/**
 * One-time auth initialization (runs on module load).
 * - Cognito mode: no-ops immediately. AuthContext handles Cognito sign-in.
 * - JWT/dev mode: if no token exists, checks /auth/status. If the backend is
 *   in dev mode, auto-obtains a dev token. This provides a zero-config DX
 *   for local development.
 *
 * NOTE: This uses raw axios (not `client`) to avoid the request interceptor
 * waiting on itself (deadlock). Do not change these to `client.get/post`.
 */
const initAuth = async () => {
    if (authInitialized) return;

    // Skip dev auto-login if Cognito is configured (auth handled by AuthContext)
    if (isCognitoConfigured()) {
        authInitialized = true;
        return;
    }

    if (!getToken()) {
        try {
            // Raw axios — bypasses the interceptor that waits on authInitPromise.
            const statusRes = await axios.get(`${API_URL}/api/v1/auth/status`);
            if (statusRes.data.auth_mode === 'dev') {
                const tokenRes = await axios.post(`${API_URL}/api/v1/auth/token/dev`, {
                    username: 'dev-user',
                    email: 'dev@localhost',
                    role: 'admin',
                    groups: ['eng-dept']
                });
                if (tokenRes.data.access_token) {
                    setToken(tokenRes.data.access_token);
                }
            }
        } catch (err) {
            // Auth init failed - will retry on 401
        }
    }
    authInitialized = true;
};

// Fire initialization immediately on module import.
// Every import of apiClient.js shares this single promise.
// Wrapped in a 10-second timeout so API calls don't hang indefinitely
// if the backend is unreachable (e.g. cold start, network issue).
const withTimeout = (promise, ms) =>
    Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error('Auth init timeout')), ms)),
    ]);

authInitPromise = withTimeout(initAuth(), 10000).catch(() => {
    console.warn('Auth init timed out after 10s, will retry on 401');
    authInitialized = true;
});

// Allows components to explicitly wait for auth before proceeding.
// Used by: CADChatPanel, DrawingList, APSViewer, CADViewerView, JobStatusIndicator,
//          UnifiedCADView, CADFileDetailDrawer (all await this before CAD API calls).
export const waitForAuth = () => authInitPromise;

// ─── Request Interceptor ────────────────────────────────────────────────────
// Runs before every outgoing request on `client`.
// 1. Waits for auth initialization (blocks until initAuth resolves).
// 2. Fetches a fresh token and attaches it as a Bearer token.
//
// Side-effect: every api.js call is delayed until initAuth completes.
// If initAuth hangs (e.g. backend unreachable in dev mode), all API calls stall.
client.interceptors.request.use(
    async (config) => {
        // Wait for auth initialization before making any request
        if (authInitPromise) {
            await authInitPromise;
        }

        // Get token (cached for 30s in cognitoAuth, instant return most of the time)
        const token = await getFreshToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// ─── 401 Response Interceptor (Token Refresh + Retry) ───────────────────────
//
// FLOW:
//   401 received
//     ├── Already refreshing? → queue this request in failedQueue
//     └── Not refreshing? → set isRefreshing=true, then:
//           ├── Cognito mode → cognitoGetIdToken() (Amplify auto-refreshes)
//           ├── JWT mode → POST /auth/refresh with stored refresh_token
//           └── Dev fallback → GET /auth/status, then POST /auth/token/dev
//         On success: processQueue() replays all queued requests with new token
//         On failure: processQueue(error) rejects all queued requests
//
// RACE CONDITION NOTE:
//   The request interceptor calls getFreshToken() → cognitoGetIdToken() on EVERY
//   request, while this interceptor also calls cognitoGetIdToken() on 401.
//   If a batch of requests fires simultaneously and the token just expired:
//   - The request interceptor may get a stale token (Amplify cache not yet updated).
//   - Multiple 401s arrive; the first triggers refresh, the rest queue.
//   - This is handled correctly by the isRefreshing + failedQueue pattern.
//   However, there is a narrow window where Amplify's fetchAuthSession in the
//   request interceptor and in this interceptor race. In practice Amplify dedupes,
//   but be aware if swapping in a custom token provider.

// Mutex flag: true while a refresh is in progress.
let isRefreshing = false;
// Requests that arrived during a refresh. Replayed once the new token is available.
let failedQueue = [];

/**
 * Replay or reject all queued requests after a refresh attempt.
 * Each entry has { resolve, reject, config } from the Promise wrapping the queued request.
 */
const processQueue = (error, token = null) => {
    failedQueue.forEach(({ resolve, reject, config }) => {
        if (error) {
            reject(error);
        } else {
            config.headers.Authorization = `Bearer ${token}`;
            resolve(client.request(config));
        }
    });
    failedQueue = [];
};

client.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalConfig = error.config;

        // ── 402 Payment Required → paywall ──
        // Billing-enabled environments return 402 from AI endpoints when the
        // user is out of credits. Surface the paywall modal (if BillingContext
        // is mounted) and pass the 402 body through to the caller so local UI
        // can also react (e.g. reset a "generate" button). No retry — the
        // caller re-invokes after the user buys more credits.
        if (error.response?.status === 402) {
            // Normalize detail to a string so any component doing
            // `err.response?.data?.detail || fallback` gets a string, not an
            // object — prevents React error #31 across all 33 raw-detail call-sites.
            const rawDetail = error.response.data?.detail;
            if (rawDetail && typeof rawDetail === 'object') {
                error.response.data.detail = rawDetail.message || JSON.stringify(rawDetail);
            }
            try {
                // Lazy require to avoid a circular import at module load time.
                // eslint-disable-next-line global-require
                const { triggerPaywall, triggerBalanceRefresh } = require('../context/BillingContext');
                const detail = error.response.data?.detail || error.response.data;
                triggerPaywall(detail);
                // Also refresh the cached balance so the badge updates immediately.
                triggerBalanceRefresh();
            } catch (err) {
                // BillingContext not mounted — silently let the 402 pass through.
                // eslint-disable-next-line no-console
                console.debug('BillingContext not available for 402 handling:', err?.message);
            }
            return Promise.reject(error);
        }

        // Only handle 401 (Unauthorized). All other errors pass through.
        if (error.response?.status === 401) {

            // If a refresh is already in flight, queue this request to be retried later.
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject, config: originalConfig });
                });
            }

            isRefreshing = true;

            try {
                // ── Strategy 1: Cognito refresh ──
                // cognitoGetIdToken → Amplify's fetchAuthSession auto-refreshes
                // the Cognito tokens using the refresh_token stored in Amplify's cache.
                if (isCognitoConfigured()) {
                    const freshToken = await cognitoGetIdToken();
                    if (freshToken) {
                        setToken(freshToken);
                        processQueue(null, freshToken);
                        isRefreshing = false;

                        // Retry the original request with the new token
                        originalConfig.headers.Authorization = `Bearer ${freshToken}`;
                        return client.request(originalConfig);
                    }
                    // No fresh token means Cognito session is fully expired → user must re-login.
                    processQueue(error);
                    isRefreshing = false;
                    return Promise.reject(error);
                }

                // ── Strategy 2: JWT refresh token ──
                // Posts the stored refresh_token to /auth/refresh for a new access_token.
                // NOTE: uses raw `axios`, not `client`, to avoid the request interceptor
                // re-attaching the expired token and potentially looping.
                const storedRefreshToken = getRefreshToken();
                if (storedRefreshToken) {
                    try {
                        const refreshRes = await axios.post(`${API_URL}/api/v1/auth/refresh`, {
                            refresh_token: storedRefreshToken
                        });

                        if (refreshRes.data.access_token) {
                            setToken(refreshRes.data.access_token);
                            if (refreshRes.data.refresh_token) {
                                setRefreshToken(refreshRes.data.refresh_token);
                            }

                            processQueue(null, refreshRes.data.access_token);
                            isRefreshing = false;

                            originalConfig.headers.Authorization = `Bearer ${refreshRes.data.access_token}`;
                            return client.request(originalConfig);
                        }
                    } catch (refreshError) {
                        // Refresh token invalid/expired — clear it and fall through to dev fallback
                        setRefreshToken(null);
                    }
                }

                // ── Strategy 3: Dev-mode auto-login fallback ──
                // If neither Cognito nor JWT refresh worked, check if the backend
                // is in dev mode and re-create a dev token.
                // This is the last resort — in production (non-dev) this path results
                // in a rejected promise and the user must re-authenticate.
                const statusRes = await axios.get(`${API_URL}/api/v1/auth/status`);
                if (statusRes.data.auth_mode === 'dev') {
                    clearToken();

                    const tokenRes = await axios.post(`${API_URL}/api/v1/auth/token/dev`, {
                        username: 'dev-user',
                        email: 'dev@localhost',
                        role: 'admin',
                        groups: ['eng-dept']
                    });

                    if (tokenRes.data.access_token) {
                        setToken(tokenRes.data.access_token);
                        if (tokenRes.data.refresh_token) {
                            setRefreshToken(tokenRes.data.refresh_token);
                        }

                        processQueue(null, tokenRes.data.access_token);
                        isRefreshing = false;

                        originalConfig.headers.Authorization = `Bearer ${tokenRes.data.access_token}`;
                        return client.request(originalConfig);
                    }
                }
            } catch (authError) {
                // All refresh strategies failed. Reject everything and redirect to login.
                processQueue(authError);
                isRefreshing = false;
                // Defensive redirect: if the user is on a protected page when their
                // auth fails (e.g. account was deleted, session revoked, or refresh
                // token expired), send them to /login. Skip if already there.
                if (typeof window !== 'undefined' && !/\/(login|register|forgot|reset|verify)/.test(window.location.pathname)) {
                    try {
                        localStorage.clear();
                        sessionStorage.clear();
                    } catch (_) { /* ignore storage errors */ }
                    window.location.href = '/login';
                }
                return Promise.reject(authError);
            }

            isRefreshing = false;
        }

        return Promise.reject(error);
    }
);

export default client;
